import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { workspaceCopy } from '../../merchant/copy.js';
import { ToastHost } from '../../merchant/ui/primitives.js';
import { MerchantMobileHeader } from './MerchantMobileHeader.js';
import { MerchantSidebar } from './MerchantSidebar.js';
import { MerchantWorkspaceShellProvider } from '../../merchant/shell-context.js';

/**
 * The authenticated merchant frame: the fixed sidebar (a drawer under
 * 1024px), the compact bar, the shared workspace snapshot, and the toast.
 * Pages render into the content column; nothing else draws navigation.
 */
export function MerchantWorkspaceShell({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  return (
    <ToastHost>
      <MerchantWorkspaceShellProvider>
        <MerchantWorkspaceShellFrame>{children}</MerchantWorkspaceShellFrame>
      </MerchantWorkspaceShellProvider>
    </ToastHost>
  );
}

function MerchantWorkspaceShellFrame({
  children,
}: {
  readonly children?: ReactNode;
}): ReactNode {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const menuTrigger = useRef<HTMLButtonElement | null>(null);
  const sidebar = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const controls = Array.from(
      sidebar.current?.querySelectorAll<HTMLElement>(
        'button,[href],[tabindex]:not([tabindex="-1"])',
      ) ?? [],
    );
    controls[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== 'Tab' || controls.length === 0) return;
      const first = controls[0];
      const last = controls.at(-1);
      if (first === undefined || last === undefined) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      menuTrigger.current?.focus();
    };
  }, [mobileOpen]);

  return (
    <div className={`mj-merchant-workspace${collapsed ? ' is-sidebar-collapsed' : ''}`}>
      <a className="mj-skip-link" href="#merchant-main-content">
        {copy.nav.skip}
      </a>
      <MerchantSidebar
        collapsed={collapsed}
        mobileOpen={mobileOpen}
        onCollapsedChange={setCollapsed}
        onMobileClose={() => {
          setMobileOpen(false);
        }}
        sidebarRef={sidebar}
      />
      <MerchantMobileHeader
        mobileOpen={mobileOpen}
        onMenuOpen={() => {
          setMobileOpen(true);
        }}
        triggerRef={menuTrigger}
      />
      <main
        id="merchant-main-content"
        className="mj-merchant-workspace-content"
        tabIndex={-1}
      >
        {children ?? <Outlet />}
      </main>
    </div>
  );
}
